import React, { useRef, useState } from "react";
import { message } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { AppDispatch } from "../../../../../../redux/store";
import { getBookingDetail } from "../../../ducks/actions";
import { LeaseTermForm, LeaseTermFormHandle } from "./components/LeaseTermForm";
import { ActionModal } from "../ActionModal";
import { PopupConfirm } from "../../../../../atoms/Popup";
import { useLeaseTermAmendments } from "../api/v1/useLeaseTermAmendments";
import { apiDateFormat } from "../../utils";
import PaymentScheduleAmendments, { PaymentScheduleFormHandle } from "./PaymentScheduleAmendments";

type LeaseActionModalProps = {
  isOpen: boolean;
  onClose: () => void;
  paymentScheduleAmendments?: string;
};

type Step = "confirmation" | "finalization" | "Confirm";

export const LeaseTermAmendment = ({ isOpen, onClose, paymentScheduleAmendments = "" }: LeaseActionModalProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const { bookingDetails } = useSelector((state: any) => state.newBooking);
  const { leaseId = bookingDetails?.leaseId } = useParams();
  const paymentScheduleRef = useRef<PaymentScheduleFormHandle>(null);

  const { submit, loading } = useLeaseTermAmendments("booking/leaseAmendments");
  let isDepositPaid = bookingDetails?.leaseStatusHistory?.find((e) => e.status === "deposit_paid");
  const leaseTermFormRef = useRef<LeaseTermFormHandle>(null);

  const [step, setStep] = useState<Step>("confirmation");
  const [leaseTerm, setLeaseTerm] = useState(null);
  const [isPaymentScheduleAmendment, setIsPaymentScheduleAmendment] = useState(false);

  const resetForm = () => {
    setStep("confirmation");
    setLeaseTerm(null);
    setIsPaymentScheduleAmendment(false);
    // Delay resetting internal state so modal closes smoothly first
    setTimeout(() => {
      onClose();
    }, 200);
  };

  const handleFormSubmit = async (body: any) => {
    
    try {
      const formattedOriginalStart = apiDateFormat(bookingDetails?.leaseTerms?.leaseStartDate);
      const formattedOriginalEnd = apiDateFormat(bookingDetails?.leaseTerms?.leaseEndDate);

      const newStart = body?.leaseTerms?.leaseStartDate;
      const newEnd = body?.leaseTerms?.leaseEndDate;

      const datesUnchanged =
        newStart === formattedOriginalStart && newEnd === formattedOriginalEnd && !isPaymentScheduleAmendment;

      // Helper: submit and handle common success behavior
      const saveLeaseTerms = async (payload: any) => {

        const response = await submit(payload);
        if (response?.code === 200) {
          leaseId && dispatch(getBookingDetail(leaseId));
          message.success("Lease terms updated successfully");
          resetForm();
        }
      };

      // ---------------------------
      // ✅ Case 1: Dates unchanged → Save directly
      // ---------------------------
      if (datesUnchanged && paymentScheduleAmendments !== "paymentScheduleAmendments" && !isDepositPaid) {
        return await saveLeaseTerms(body);
      }

      if (paymentScheduleAmendments === "paymentScheduleAmendments") {
        const payload = {
          leaseId,
          paymentSchedule: body.paymentSchedule,
        };
        return await saveLeaseTerms(payload);
      }
      // ---------------------------
      // ✅ Case 2: Dates changed but no paymentSchedule → Ask user
      // ---------------------------
      if (!body.paymentSchedule) {
        if (isDepositPaid) {
          return PopupConfirm({
            title: "Would you like to amend the lease terms.",
            cancelText: "No",
            okText: "Yes",
            onOk: async () => await saveLeaseTerms(body),
          });
        } else {
          setLeaseTerm(body);

          return PopupConfirm({
            title:
              "Would you like to amend the payment schedule? If you choose No, your current changes will be saved as they are.",
            cancelText: "No",
            okText: "Yes",
            onOk: () => setIsPaymentScheduleAmendment(true),
            onCancel: async () => await saveLeaseTerms(body),
          });
        }
      }

      // ---------------------------
      // ✅ Case 3: Dates changed + payment schedule present → Save
      // ---------------------------

      if (!isDepositPaid) {
        const payload = {
          ...leaseTerm,
          paymentSchedule: body.paymentSchedule,
        };

        return PopupConfirm({
          title: `You are changing the lease term from ( ${formattedOriginalStart} - ${formattedOriginalEnd} ) to ( ${leaseTerm?.leaseTerms?.leaseStartDate} - ${leaseTerm?.leaseTerms?.leaseEndDate} ) . Are you sure?`,
          cancelText: "No",
          okText: "Yes",
          onOk: async () => await saveLeaseTerms(payload),
        });
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleOk = () => {
    if (step === "confirmation") {
      setStep("finalization");
    } else if (isPaymentScheduleAmendment || paymentScheduleAmendments === "paymentScheduleAmendments") {
      paymentScheduleRef.current?.submitForm();
    } else {
      // Trigger child form submit via ref
      leaseTermFormRef.current?.submitForm();
    }
  };

  return (
    <ActionModal
      isOpen={isOpen}
      onClose={resetForm}
      title={
        paymentScheduleAmendments === "paymentScheduleAmendments"
          ? "Payment Schedule Amendments"
          : "Lease Term Amendments"
      }
      steps={step === "confirmation" ? "Confirm" : "Submit"}
      handleOk={handleOk}
      resetForm={resetForm}
      confirmLoading={loading}
      width={step === "confirmation" ? 600 : 1400}
    >
      {isOpen && step === "confirmation" && (
        <div>
          {paymentScheduleAmendments !== "paymentScheduleAmendments" ? (
            <>
              <p>You are about to amend the lease term.</p>
              <p>
                This action may change the start date, end date, rent-free period, and the overall duration of the
                lease.
              </p>
            </>
          ) : (
            <>
              <p>You are about to amend the payment schedule.</p>
            </>
          )}

          <p>These changes may also impact the payment schedule. Do you want to proceed?</p>
        </div>
      )}
      {isOpen &&
        !isPaymentScheduleAmendment &&
        step === "finalization" &&
        paymentScheduleAmendments !== "paymentScheduleAmendments" && (
          <LeaseTermForm ref={leaseTermFormRef} step={step} onSubmit={handleFormSubmit} />
        )}

      {isOpen &&
        (isPaymentScheduleAmendment ||
          (paymentScheduleAmendments === "paymentScheduleAmendments" && step === "finalization")) && (
          <PaymentScheduleAmendments
            paymentScheduleAmendments={paymentScheduleAmendments}
            leaseTerms={leaseTerm}
            ref={paymentScheduleRef}
            onSubmit={handleFormSubmit}
          />
        )}
    </ActionModal>
  );
};
function asyc() {
  throw new Error("Function not implemented.");
}
